import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import TagsInput from "../../../../components/common/TagsInput";
import RentalSurveySummaryHeader from "./RentalSurveySummaryHeader";
import RentalSurveyTable from "./RentalSurveyTable";
import {
  calculateOccupancyMetrics,
  createBlankNote,
  formatDateTimeLabel,
  hasNoteText,
  noteMatchesFilter,
  normalizeNotes,
  normalizeTags,
  toTextOrEmpty,
} from "./utils";

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#F8FAFC",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "#E2E8F0",
    },
    "&:hover fieldset": {
      borderColor: "#CBD5E1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3B82F6",
    },
  },
  "& .MuiInputBase-inputMultiline": {
    resize: "vertical",
    overflow: "auto",
  },
};

const HEADER_ICON_BUTTON_SX = {
  color: "#64748B",
  p: 0.35,
};

const HEADER_DELETE_BUTTON_SX = {
  color: "#64748B",
  p: 0.35,
  "&:hover": {
    backgroundColor: "#fee2e2",
    color: "#ef4444",
  },
};

const NOTE_META_SX = {
  fontSize: "11px",
  fontWeight: 600,
  color: "#64748B",
  lineHeight: 1.2,
};

// Edits within this window of the note's creation are not labelled "edited".
const EDIT_LABEL_THRESHOLD_MS = 60 * 1000;

const wasEdited = (note) => {
  const created = new Date(note?.createdAt).getTime();
  const updated = new Date(note?.updatedAt).getTime();
  if (!Number.isFinite(created) || !Number.isFinite(updated)) return false;
  return updated - created > EDIT_LABEL_THRESHOLD_MS;
};

const RentalSurveyHistoryCard = ({ entry, onUpdate, onDelete, tagOptions = [], noteFilter = null }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [focusNoteId, setFocusNoteId] = useState(null);
  const noteInputRefs = useRef({});

  const notes = useMemo(() => normalizeNotes(entry?.notes, entry?.surveyDate), [entry?.notes, entry?.surveyDate]);
  const visibleNotes = useMemo(
    () => notes.filter((note) => noteMatchesFilter(note, noteFilter)),
    [notes, noteFilter]
  );

  useEffect(() => {
    if (!focusNoteId || collapsed) return;

    const timer = setTimeout(() => {
      const input = noteInputRefs.current[focusNoteId];
      if (input && typeof input.focus === "function") {
        input.focus();
        if (typeof input.setSelectionRange === "function") {
          const length = typeof input.value === "string" ? input.value.length : 0;
          input.setSelectionRange(length, length);
        }
      }
      setFocusNoteId(null);
    }, 240);

    return () => clearTimeout(timer);
  }, [collapsed, focusNoteId, notes]);

  const occupancyMetrics = useMemo(
    () => calculateOccupancyMetrics(entry?.rows || []),
    [entry?.rows]
  );

  const displayEntry = useMemo(
    () => ({
      ...entry,
      notes,
      occupancyPercent: occupancyMetrics.occupancyPercent,
      averageOccupancyPercent: occupancyMetrics.averageOccupancyPercent,
    }),
    [entry, notes, occupancyMetrics.averageOccupancyPercent, occupancyMetrics.occupancyPercent]
  );

  const handleFieldChange = (key, value) => {
    let nextValue = value;

    if (key === "surveyDate") {
      nextValue = value || null;
    }

    onUpdate({
      ...displayEntry,
      [key]: nextValue,
    });
  };

  const handleRowsChange = (rows) => {
    const metrics = calculateOccupancyMetrics(rows);
    onUpdate({
      ...displayEntry,
      rows,
      occupancyPercent: metrics.occupancyPercent,
      averageOccupancyPercent: metrics.averageOccupancyPercent,
    });
  };

  const handleAddNote = () => {
    const note = createBlankNote();
    setCollapsed(false);
    setFocusNoteId(note.id);
    handleFieldChange("notes", [note, ...notes]);
  };

  const handleNoteTextChange = (noteId, text) => {
    handleFieldChange(
      "notes",
      notes.map((note) =>
        note.id === noteId
          ? { ...note, text: toTextOrEmpty(text), updatedAt: new Date().toISOString() }
          : note
      )
    );
  };

  const handleNoteTagsChange = (noteId, tags) => {
    handleFieldChange(
      "notes",
      notes.map((note) =>
        note.id === noteId
          ? { ...note, tags: normalizeTags(tags), updatedAt: new Date().toISOString() }
          : note
      )
    );
  };

  const handleRemoveNote = (noteId) => {
    delete noteInputRefs.current[noteId];
    handleFieldChange("notes", notes.filter((note) => note.id !== noteId));
  };

  const handleOpenNotes = () => {
    setCollapsed(false);
    if (notes.length > 0) {
      setFocusNoteId(notes[0].id);
    }
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  const headerActions = (
    <>
      <Tooltip title="Delete Snapshot">
        <IconButton size="small" onClick={onDelete} sx={HEADER_DELETE_BUTTON_SX}>
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title={collapsed ? "Expand" : "Collapse"}>
        <IconButton size="small" onClick={toggleCollapsed} sx={HEADER_ICON_BUTTON_SX}>
          {collapsed ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ExpandLessIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid #DCE3EA",
        boxShadow: "0 2px 10px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
      }}
    >
      <RentalSurveySummaryHeader
        entry={displayEntry}
        actions={headerActions}
        onSurveyDateChange={(nextValue) => handleFieldChange("surveyDate", nextValue)}
      />

      <Collapse in={!collapsed} timeout="auto" unmountOnExit={false}>
        <CardContent sx={{ p: 2 }}>
          <RentalSurveyTable
            rows={displayEntry?.rows || []}
            onRowsChange={handleRowsChange}
            hasNotes={hasNoteText(notes)}
            onAddNotes={handleAddNote}
            onOpenNotes={handleOpenNotes}
          />

          {visibleNotes.length > 0 ? (
            <Stack spacing={1.25} sx={{ mt: 1.5 }}>
              {visibleNotes.map((note) => (
                <Box key={note.id}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography sx={NOTE_META_SX}>
                      {formatDateTimeLabel(note.createdAt)}
                      {wasEdited(note) ? ` (edited ${formatDateTimeLabel(note.updatedAt)})` : ""}
                    </Typography>
                    <Tooltip title="Remove note">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveNote(note.id)}
                        sx={HEADER_DELETE_BUTTON_SX}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={16}
                    placeholder="Note"
                    value={note.text}
                    onChange={(event) => handleNoteTextChange(note.id, event.target.value)}
                    inputRef={(element) => {
                      if (element) {
                        noteInputRefs.current[note.id] = element;
                      } else {
                        delete noteInputRefs.current[note.id];
                      }
                    }}
                    sx={INPUT_SX}
                  />
                  <TagsInput
                    value={note.tags}
                    options={tagOptions}
                    onChange={(tags) => handleNoteTagsChange(note.id, tags)}
                    sx={{ mt: 0.75 }}
                  />
                </Box>
              ))}
            </Stack>
          ) : null}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default RentalSurveyHistoryCard;
